// Hindi and English names for every grammar category. Labels are facts, not generated text.

import type {
    Case,
    Gender,
    KridantaKind,
    Lang,
    Num,
    Person,
    StemClass,
    Tense
} from '@/prakrit/lib/grammar/types'

export const PERSON_LABEL: Record<Person, Lang> = {
    uttam: { hi: 'उत्तम पुरुष', en: 'speaker (उत्तम)' },
    madhyam: { hi: 'मध्यम पुरुष', en: 'person addressed (मध्यम)' },
    anya: { hi: 'अन्य पुरुष', en: 'person spoken about (अन्य)' }
}

export const PERSON_SHORT: Record<Person, Lang> = {
    uttam: { hi: 'उत्तम', en: 'speaker' },
    madhyam: { hi: 'मध्यम', en: 'addressee' },
    anya: { hi: 'अन्य', en: 'the other' }
}

/** How a person maps to English grammar, for the explanation card. */
export const PERSON_ENGLISH_NOTE: Record<Person, Lang> = {
    uttam: { hi: 'जो बोल रहा है — मैं, हम', en: 'the speaker: I, we (English "first person")' },
    madhyam: { hi: 'जिससे बात हो रही है — तुम, तुम सब', en: 'the one spoken to: you (English "second person")' },
    anya: { hi: 'जिसके बारे में बात हो रही है — वह, वे', en: 'the one spoken about: he, she, it, they (English "third person")' }
}

export const NUMBER_LABEL: Record<Num, Lang> = {
    eka: { hi: 'एकवचन', en: 'singular' },
    bahu: { hi: 'बहुवचन', en: 'plural' }
}

export const GENDER_LABEL: Record<Gender, Lang> = {
    pu: { hi: 'पुल्लिंग', en: 'masculine' },
    stri: { hi: 'स्त्रीलिंग', en: 'feminine' },
    napum: { hi: 'नपुंसकलिंग', en: 'neuter' }
}

export const GENDER_SHORT: Record<Gender, string> = { pu: 'पु.', stri: 'स्त्री.', napum: 'नपुं.' }

export const CASE_LABEL: Record<Case, Lang> = {
    prathama: { hi: 'प्रथमा', en: 'nominative' },
    dvitiya: { hi: 'द्वितीया', en: 'accusative' },
    tritiya: { hi: 'तृतीया', en: 'instrumental' },
    chaturthi: { hi: 'चतुर्थी', en: 'dative' },
    panchami: { hi: 'पंचमी', en: 'ablative' },
    shashthi: { hi: 'षष्ठी', en: 'genitive' },
    saptami: { hi: 'सप्तमी', en: 'locative' },
    sambodhan: { hi: 'सम्बोधन', en: 'vocative' }
}

/** कारक — the role a case plays in the sentence. */
export const KARAK: Record<Case, Lang> = {
    prathama: { hi: 'कर्ता', en: 'the doer (subject)' },
    dvitiya: { hi: 'कर्म', en: 'the object' },
    tritiya: { hi: 'करण (के द्वारा, साथ)', en: 'by / with' },
    chaturthi: { hi: 'सम्प्रदान (के लिए)', en: 'for / to' },
    panchami: { hi: 'अपादान (से)', en: 'from' },
    shashthi: { hi: 'सम्बन्ध (का, के, की)', en: "of / 's" },
    saptami: { hi: 'अधिकरण (में, पर)', en: 'in / on' },
    sambodhan: { hi: 'सम्बोधन (हे …)', en: 'calling (O …)' }
}

export const TENSE_LABEL: Record<Tense, Lang> = {
    vartaman: { hi: 'वर्तमान काल', en: 'present' },
    bhoot: { hi: 'भूत काल', en: 'past' },
    bhavishya: { hi: 'भविष्यत् काल', en: 'future' },
    vidhi: { hi: 'विधि एवं आज्ञा', en: 'wish and command' },
    karmani_vartaman: { hi: 'कर्मवाच्य — वर्तमान', en: 'passive, present' },
    karmani_bhoot: { hi: 'कर्मवाच्य — भूत', en: 'passive, past' },
    karmani_vidhi: { hi: 'कर्मवाच्य — विधि/आज्ञा', en: 'passive, command' },
    prerak_vartaman: { hi: 'प्रेरणार्थक — वर्तमान', en: 'causative, present' },
    prerak_bhoot: { hi: 'प्रेरणार्थक — भूत', en: 'causative, past' },
    prerak_bhavishya: { hi: 'प्रेरणार्थक — भविष्यत्', en: 'causative, future' },
    prerak_vidhi: { hi: 'प्रेरणार्थक — विधि/आज्ञा', en: 'causative, command' },
    kriyatipatti: { hi: 'क्रियातिपत्ति', en: 'conditional' }
}

export const TENSE_SHORT: Record<Tense, Lang> = {
    vartaman: { hi: 'वर्तमान', en: 'present' },
    bhoot: { hi: 'भूत', en: 'past' },
    bhavishya: { hi: 'भविष्यत्', en: 'future' },
    vidhi: { hi: 'विधि-आज्ञा', en: 'command' },
    karmani_vartaman: { hi: 'कर्मणि वर्तमान', en: 'passive present' },
    karmani_bhoot: { hi: 'कर्मणि भूत', en: 'passive past' },
    karmani_vidhi: { hi: 'कर्मणि आज्ञा', en: 'passive command' },
    prerak_vartaman: { hi: 'प्रेरक वर्तमान', en: 'causative present' },
    prerak_bhoot: { hi: 'प्रेरक भूत', en: 'causative past' },
    prerak_bhavishya: { hi: 'प्रेरक भविष्यत्', en: 'causative future' },
    prerak_vidhi: { hi: 'प्रेरक आज्ञा', en: 'causative command' },
    kriyatipatti: { hi: 'क्रियातिपत्ति', en: 'conditional' }
}

export const KRIDANTA_LABEL: Record<KridantaKind, Lang> = {
    sambandh: { hi: 'सम्बन्ध कृदन्त (-कर)', en: 'having done' },
    hetvarth: { hi: 'हेत्वर्थ कृदन्त (के लिए)', en: 'in order to' },
    vartaman: { hi: 'वर्तमान कृदन्त (-ता हुआ)', en: 'doing (-ing)' },
    bhoot: { hi: 'भूत कृदन्त (-आ हुआ)', en: 'done' },
    bhavishya: { hi: 'भविष्य कृदन्त', en: 'about to be' },
    vidhi: { hi: 'विधि कृदन्त (-ने योग्य)', en: 'to be done' },
    karmani_vartaman: { hi: 'कर्मवाच्य वर्तमान कृदन्त', en: 'being done' },
    karmani_bhavishya: { hi: 'कर्मवाच्य भविष्य कृदन्त', en: 'about to be done' },
    prerak_sambandh: { hi: 'प्रेरक सम्बन्ध कृदन्त', en: 'having caused to do' },
    prerak_hetvarth: { hi: 'प्रेरक हेत्वर्थ कृदन्त', en: 'in order to cause to do' },
    prerak_vidhi: { hi: 'प्रेरक विधि कृदन्त', en: 'to be caused to do' },
    prerak_vartaman: { hi: 'प्रेरक वर्तमान कृदन्त', en: 'causing to do' },
    prerak_bhoot: { hi: 'प्रेरक भूत कृदन्त', en: 'caused to do' }
}

export const STEM_CLASS_LABEL: Record<StemClass, Lang> = {
    'a': { hi: 'अकारान्त', en: 'ending in अ' },
    'ā': { hi: 'आकारान्त', en: 'ending in आ' },
    'i': { hi: 'इकारान्त', en: 'ending in इ' },
    'ī': { hi: 'ईकारान्त', en: 'ending in ई' },
    'u': { hi: 'उकारान्त', en: 'ending in उ' },
    'ū': { hi: 'ऊकारान्त', en: 'ending in ऊ' },
    'e': { hi: 'एकारान्त', en: 'ending in ए' },
    'o': { hi: 'ओकारान्त', en: 'ending in ओ' }
}

/** The subject pronoun that names each verb cell (book tables: अहं / अम्हे …). */
export const SUBJECT: Record<Person, Record<Num, string>> = {
    uttam: { eka: 'अहं', bahu: 'अम्हे' },
    madhyam: { eka: 'तुमं', bahu: 'तुम्हे' },
    anya: { eka: 'सो', bahu: 'ते' }
}

export const SUBJECT_GLOSS: Record<Person, Record<Num, Lang>> = {
    uttam: { eka: { hi: 'मैं', en: 'I' }, bahu: { hi: 'हम', en: 'we' } },
    madhyam: { eka: { hi: 'तुम', en: 'you' }, bahu: { hi: 'तुम सब', en: 'you all' } },
    anya: { eka: { hi: 'वह', en: 'he / she' }, bahu: { hi: 'वे', en: 'they' } }
}

export const POS_LABEL: Record<string, Lang> = {
    dhatu: { hi: 'धातु', en: 'verb root' },
    noun: { hi: 'संज्ञा', en: 'noun' },
    avyaya: { hi: 'अव्यय', en: 'indeclinable' },
    pronoun: { hi: 'सर्वनाम', en: 'pronoun' },
    number: { hi: 'संख्या', en: 'number' }
}
